/**
 * `{{group.key}}` placeholders in canned responses and `add_comment` post-functions
 * (S-05-08, S-05-05). Pure string rendering: known paths are replaced, unknown paths are
 * left verbatim so a typo is visible in the rendered text instead of silently vanishing.
 */
export interface PlaceholderContext {
  ticket?: {
    key?: string;
    number?: number;
    summary?: string;
    status?: string;
    priority?: string;
  };
  requester?: { name?: string; email?: string };
  currentUser?: { displayName?: string; email?: string };
  project?: { name?: string; key?: string };
}

/** The placeholders documented for authors; others resolve when the context carries them. */
export const KNOWN_PLACEHOLDERS = [
  'ticket.key',
  'ticket.number',
  'ticket.summary',
  'ticket.status',
  'ticket.priority',
  'requester.name',
  'requester.email',
  'currentUser.displayName',
  'currentUser.email',
  'project.name',
  'project.key',
] as const;

export const PLACEHOLDER_PATTERN =
  /\{\{\s*([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\s*\}\}/g;

function lookup(ctx: PlaceholderContext, path: string): unknown {
  let current: unknown = ctx;
  for (const segment of path.split('.')) {
    if (current === null || typeof current !== 'object') {
      return undefined;
    }
    if (!Object.hasOwn(current, segment)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

/**
 * Replaces every `{{path}}` whose value is a string, number or boolean; `null` renders as an
 * empty string; unknown paths and object values stay as written.
 */
export function renderPlaceholders(text: string, ctx: PlaceholderContext): string {
  return text.replace(PLACEHOLDER_PATTERN, (match, path: string) => {
    const value = lookup(ctx, path);
    if (value === null) {
      return '';
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return match;
  });
}

/** Distinct placeholder paths used in a text, in order of first appearance. */
export function collectPlaceholders(text: string): string[] {
  const out: string[] = [];
  for (const match of text.matchAll(PLACEHOLDER_PATTERN)) {
    const path = match[1];
    if (path && !out.includes(path)) {
      out.push(path);
    }
  }
  return out;
}
